/*
 * QUI MONTRE CETTE FAMILLE : la reponse du shell au port de navigation du socle.
 *
 * LE PROBLEME QU'IL FERME. Une colonne `lien` designe une FAMILLE
 * (`contacts/organization`), jamais une app ; quand aucune porte de l'ecran
 * courant ne montre cette famille, l'ecran affiche le NOM (par l'annuaire) mais
 * ne sait pas OU la fiche s'ouvre. Le seul qui le sache est le shell : il tient
 * la liste des apps installees, leurs ecrans declares, et ce que ce membre peut
 * ouvrir. Ce module repond a la question, et rien d'autre.
 *
 * TROIS FONCTIONS PURES, testables sans React : `portesDeclarees` lit les portes
 * du PREMIER ecran d'une declaration (une app en monte UN, `resolution.ts` de
 * `@taki/app-runtime` le rejoue), `destinationPour` choisit l'app, et
 * `origineDuSaut` dit a l'arrivee d'ou l'on vient (le RETOUR fait partie du
 * saut : une fiche ouverte par un lien y ramene, regle du fondateur du
 * 09/09/2026). La lecture
 * est STRUCTURELLE, comme tout `modele.ts` : ce package n'a pas
 * `@taki/data-model`, et une declaration qui ne se relit pas n'est pas une
 * panne ici, c'est zero porte, donc zero destination, donc pas de fleche.
 *
 * LA REGLE DE CHOIX, la MEME que `porteQuiMontre` cote `@taki/ui` (screens.ts) :
 * une porte dont le jeu est EXACTEMENT la famille et le type gagne ; a defaut,
 * une porte SANS type local (elle montre toute la famille) convient ; une porte
 * typee sur un AUTRE type ne convient jamais, elle n'ouvrirait pas la fiche.
 * Entre deux apps qui conviennent au meme rang, la PREMIERE de la liste
 * (l'ordre de `installedApps`) l'emporte : un ordre stable, que l'org tient.
 * Le `type` recu est celui de la reference quand elle en fixe un, sinon celui
 * de la FICHE visee, que `@taki/ui` lit dans son annuaire : une reference nue
 * vers `contacts` ouvre donc bien Societes ou Personnes selon la fiche.
 */
import type { DestinationDeFiche } from '@taki/ui';
import type { ShellAppEntry } from './types.js';

/** Une porte lue dans une declaration : son id et le jeu qu'elle montre. */
export interface PorteDeclareeLue {
  id: string;
  family: string;
  type?: string;
  /** Son ajout tenu par le socle (action `add` sans `fn`), quand elle en declare un. */
  creation?: { actionId: string };
}

const estObjet = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const chaine = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined;

/**
 * LES PORTES DU PREMIER ECRAN d'un `taki.screens.json`, lues sans schema.
 * Une porte sans `jeu` (un canvas, un placeholder) ne montre aucune famille et
 * n'est pas rendue. Toute forme inattendue rend `[]`.
 */
export function portesDeclarees(ecrans: unknown): PorteDeclareeLue[] {
  if (!estObjet(ecrans) || !Array.isArray(ecrans.ecrans)) return [];
  const premier: unknown = ecrans.ecrans[0];
  if (!estObjet(premier) || !Array.isArray(premier.portes)) return [];
  const portes: PorteDeclareeLue[] = [];
  for (const porte of premier.portes as unknown[]) {
    if (!estObjet(porte)) continue;
    const id = chaine(porte.id);
    const jeu = porte.jeu;
    if (id === undefined || !estObjet(jeu)) continue;
    const family = chaine(jeu.family);
    if (family === undefined) continue;
    const type = chaine(jeu.type);
    /* LA PREMIERE ACTION « add » SANS EFFET D'APP : c'est la fiche vide du
       socle, la seule creation qui sache rendre l'id cree au champ d'un lien. */
    const ajout = Array.isArray(porte.actions)
      ? (porte.actions as unknown[]).find(
          (action) =>
            estObjet(action) &&
            action.kind === 'add' &&
            chaine(action.id) !== undefined &&
            action.fn === undefined,
        )
      : undefined;
    const actionId = estObjet(ajout) ? chaine(ajout.id) : undefined;
    portes.push({
      id,
      family,
      ...(type === undefined ? {} : { type }),
      ...(actionId === undefined ? {} : { creation: { actionId } }),
    });
  }
  return portes;
}

/**
 * Une app CANDIDATE : l'entree du shell et la declaration EFFECTIVE de ses
 * ecrans, celle de l'org si elle en a une, celle du bundle sinon. C'est
 * l'appelant qui fait ce choix (il tient le port des declarations) ; ici on ne
 * lit que ce qu'on recoit.
 */
export interface AppCandidate {
  entry: Pick<ShellAppEntry, 'id' | 'label'>;
  ecrans: unknown;
}

/**
 * LE SAUT QU'ON VIENT DE FAIRE, tel que le shell le retient : ou il a mene, et
 * le titre de la fiche qu'on a quittee.
 *
 * POURQUOI LE SHELL LE PORTE. Un saut entre apps DEMONTE l'ecran de depart, et
 * l'ecran d'arrivee n'a jamais rien su de lui : la memoire ne peut vivre que
 * chez celui qui survit aux deux. Elle ne sert qu'a la puce de sortie de la
 * fiche d'arrivee (« Retour a <la fiche d'ou l'on vient> »).
 */
export interface SautEntreApps {
  /** L'app d'ARRIVEE : personne d'autre n'a le droit de relire cette origine. */
  app: string;
  /** La fiche d'ARRIVEE : la puce ne parle que sur celle-la. */
  id: string;
  /** Le titre de la fiche QUITTEE. Jamais vide : une puce muette n'existe pas. */
  titre: string;
}

/**
 * LE TITRE A DIRE quand l'app `app` ouvre la fiche `id`, ou `null`.
 *
 * DEUX CONDITIONS, ET LES DEUX COMPTENT : la meme app (sinon une fiche de meme
 * id ailleurs heriterait d'un retour qui n'est pas le sien) et la meme fiche
 * (les fleches precedent/suivant de la rangee 3 quittent le chemin du saut).
 */
export function origineDuSaut(
  saut: SautEntreApps | null,
  app: string,
  id: string,
): string | null {
  if (saut == null) return null;
  return saut.app === app && saut.id === id ? saut.titre : null;
}

/**
 * L'APP ET LA PORTE QUI MONTRENT `family` (+ `type`), ou `null`.
 * Les candidates sont deja FILTREES par l'appelant (deprecated, acces, masquage
 * dev) : ce module ne juge aucun droit.
 */
export function destinationPour(
  candidates: readonly AppCandidate[],
  family: string,
  type?: string,
): DestinationDeFiche | null {
  let sansType: DestinationDeFiche | null = null;
  for (const { entry, ecrans } of candidates) {
    for (const porte of portesDeclarees(ecrans)) {
      if (porte.family !== family) continue;
      const destination: DestinationDeFiche = {
        app: entry.id,
        appLabel: entry.label,
        porte: porte.id,
        ...(porte.creation === undefined ? {} : { creation: porte.creation }),
      };
      if (porte.type === type) return destination;
      if (porte.type === undefined && sansType === null) sansType = destination;
    }
  }
  return sansType;
}
